import { ChangeDetectionStrategy, Component, computed, input, model, output, signal } from '@angular/core';
import { APP } from 'src/app/config/app';
import { getClientDataForm } from 'src/app/forms/superAdminForms';
import { DetailFormDraft, IBase, IClientDataDraft, IClientDetail } from 'src/app/models';
import { ClientsService, TownsService, UtilsService } from 'src/app/services';
import { DetailFormComponent } from 'src/app/shared/detail-form/detail-form.component';
import { FormActionsComponent } from 'src/app/shared/form-actions/form-actions.component';

@Component({
  selector: 'app-client-data-form',
  templateUrl: './client-data-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DetailFormComponent, FormActionsComponent],
})
export class ClientDataFormComponent {
  readonly client = input<IClientDetail | null>(null);
  readonly edit = model(false);
  readonly saved = output<IClientDetail>();
  readonly cancelled = output();

  readonly draft = new DetailFormDraft<IClientDataDraft>(() => {
    const client = this.client();
    return {
      name: client?.name ?? '',
      code: client?.code ?? '',
      taxNumber: client?.taxNumber ?? '',
      address: client?.address ?? '',
      clientType: client?.clientType ?? null,
      defaultTownId: client?.defaultTown?.id ?? null,
    };
  });
  readonly fields = computed(() =>
    getClientDataForm(this.draft.value(), this.client()?.defaultTown ?? null, this.edit(), this.loadTowns),
  );
  readonly isValid = computed(() => {
    const draft = this.draft.value();
    return !!draft.name.trim() && !!draft.code.trim() && !!draft.clientType && draft.defaultTownId !== null;
  });
  readonly saving = signal(false);

  private readonly loadTowns = async (searchValue: string): Promise<IBase[]> => {
    const towns = await this.townsService.filterTowns(1, APP.SUPERADMIN_SELECT_OPTIONS_PAGE_SIZE, searchValue);
    return towns.content as IBase[];
  };

  constructor(
    private clientsService: ClientsService,
    private townsService: TownsService,
    private utilsService: UtilsService,
  ) {}

  cancel() {
    this.draft.reset();
    this.edit.set(false);
    this.cancelled.emit();
  }

  async save() {
    if (!this.isValid() || this.saving()) return;
    const draft = this.draft.value();
    this.saving.set(true);
    try {
      const savedClient = await this.clientsService.saveClient(
        {
          name: draft.name.trim(),
          code: draft.code.trim(),
          taxNumber: this.utilsService.toOptionalText(draft.taxNumber),
          address: this.utilsService.toOptionalText(draft.address),
          clientType: draft.clientType!,
          defaultTownId: Number(draft.defaultTownId),
        },
        this.client()?.id,
      );
      this.utilsService.showSuccessNotification('superadminZone.saved');
      this.edit.set(false);
      this.saved.emit(savedClient);
    } finally {
      this.saving.set(false);
    }
  }
}
